import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { usePerDiem, useCategories, useRevenueCategories, useHousehold } from '../hooks/useApi'
import { formatCurrency } from '../lib/utils'
import { useTheme } from '../context/ThemeContext'
import { useAuth } from '../context/AuthContext'
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Switch } from '../components/ui/switch'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog'
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog'
import { toast } from 'sonner'
import { Sun, Moon, DollarSign, Tag, Plus, Trash2, Loader2, User, Users, Mail, UserMinus, Shield, Clock, Check, Lock, Truck } from 'lucide-react'

// Admin email(s) that can see the admin dashboard link
const ADMIN_EMAILS = ['twilatee@gmail.com']

export default function Settings() {
  const navigate = useNavigate()
  const { theme, toggleTheme, isDark } = useTheme()
  const { user, isOwner } = useAuth()
  const [perDiemSettings, setPerDiemSettings] = useState({ partial_rate: 60, full_rate: 80 })
  const [categories, setCategories] = useState([])
  const [newCategoryName, setNewCategoryName] = useState('')
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  
  // Revenue categories state
  const [revenueCategories, setRevenueCategories] = useState([])
  const [newRevCategoryName, setNewRevCategoryName] = useState('')
  const [revCategoryDialogOpen, setRevCategoryDialogOpen] = useState(false)
  
  // Household/linked users state
  const [householdUsers, setHouseholdUsers] = useState([])
  const [pendingInvites, setPendingInvites] = useState([])
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteSuccess, setInviteSuccess] = useState(false)
  const [removeUserId, setRemoveUserId] = useState(null)
  
  const { fetchSettings, updateSettings } = usePerDiem()
  const { fetchCategories, createCategory, deleteCategory } = useCategories()
  const { fetchRevenueCategories, createRevenueCategory, deleteRevenueCategory } = useRevenueCategories()
  const { fetchUsers, createInvite, fetchInvites, deleteInvite, removeUser, loading: householdLoading } = useHousehold()

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    try {
      const [settings, cats, revCats] = await Promise.all([
        fetchSettings(),
        fetchCategories(),
        fetchRevenueCategories()
      ])
      setPerDiemSettings(settings)
      setCategories(cats)
      setRevenueCategories(revCats)
      
      // Load household data
      await loadHouseholdData()
    } catch (err) {
      console.error('Failed to load settings:', err)
    }
  }

  const loadHouseholdData = async () => {
    try {
      const users = await fetchUsers()
      setHouseholdUsers(users)
      
      if (isOwner) {
        const invites = await fetchInvites()
        setPendingInvites(invites)
      }
    } catch (err) {
      console.error('Failed to load household data:', err)
    }
  }

  const handleSavePerDiem = async () => {
    setSaving(true)
    try {
      await updateSettings(perDiemSettings)
      toast.success('Per diem rates updated')
    } catch (err) {
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleAddCategory = async () => {
    if (!newCategoryName.trim()) {
      toast.error('Please enter a category name')
      return
    }
    
    try {
      await createCategory({ name: newCategoryName.trim(), icon: 'tag' })
      toast.success('Category added')
      setNewCategoryName('')
      setCategoryDialogOpen(false)
      loadData()
    } catch (err) {
      toast.error('Failed to add category')
    }
  }

  const handleDeleteCategory = async (id) => {
    try {
      await deleteCategory(id)
      toast.success('Category deleted')
      loadData()
    } catch (err) {
      toast.error('Cannot delete default categories')
    }
  }

  const handleAddRevenueCategory = async () => {
    if (!newRevCategoryName.trim()) {
      toast.error('Please enter a category name')
      return
    }
    try {
      await createRevenueCategory({ name: newRevCategoryName.trim() })
      toast.success('Revenue category added')
      setNewRevCategoryName('')
      setRevCategoryDialogOpen(false)
      loadData()
    } catch (err) {
      toast.error('Failed to add revenue category')
    }
  }

  const handleDeleteRevenueCategory = async (id) => {
    try {
      await deleteRevenueCategory(id)
      toast.success('Revenue category deleted')
      loadData()
    } catch (err) {
      toast.error('Cannot delete default revenue categories')
    }
  }

  const handleCreateInvite = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email address')
      return
    }
    
    try {
      await createInvite(inviteEmail.trim())
      setInviteSuccess(true)
      toast.success('Invite sent!')
      loadHouseholdData()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create invite')
    }
  }

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false)
    setInviteEmail('')
    setInviteSuccess(false)
  }

  const handleDeleteInvite = async (id) => {
    try {
      await deleteInvite(id)
      toast.success('Invite cancelled')
      loadHouseholdData()
    } catch (err) {
      toast.error('Failed to cancel invite')
    }
  }

  const handleRemoveUser = async () => {
    try {
      await removeUser(removeUserId)
      toast.success('User removed from account')
      setRemoveUserId(null)
      loadHouseholdData()
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove user')
    }
  }

  const customCategories = categories.filter(c => !c.is_default)
  const customRevenueCategories = revenueCategories.filter(c => !c.is_default)
  
  // Calculate if can invite more (max 2 additional = 3 total)
  const totalUsers = householdUsers.length + pendingInvites.length
  const canInviteMore = totalUsers < 3

  return (
    <div className="space-y-6 animate-fade-in" data-testid="settings-page">
      <div className="space-y-1">
        <h1 className="text-3xl font-bold tracking-tight font-['Barlow_Condensed']">
          SETTINGS
        </h1>
        <p className="text-muted-foreground">
          Manage your preferences, rates, and linked users
        </p>
      </div>

      {/* User Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <User className="w-5 h-5" />
            ACCOUNT
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Name</span>
              <span className="font-medium">{user?.name}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{user?.email}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Role</span>
              <span className="font-medium capitalize">{user?.role}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Account Type</span>
              <span className="font-medium flex items-center gap-1">
                {isOwner ? (
                  <>
                    <Shield className="w-4 h-4 text-primary" />
                    Owner
                  </>
                ) : (
                  'Member'
                )}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Admin Dashboard Link - Only visible to admin emails */}
      {user?.email && ADMIN_EMAILS.includes(user.email.toLowerCase()) && (
        <Card className="border-primary/30 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <Lock className="w-5 h-5 text-primary" />
              APP OWNER
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Access the admin dashboard to view all registered users and activity metrics.
            </p>
            <Button
              onClick={() => navigate('/admin')}
              className="w-full h-12 rounded-full font-semibold"
              data-testid="admin-dashboard-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Admin Dashboard
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Linked Users / Household */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <Users className="w-5 h-5" />
              LINKED USERS
            </CardTitle>
            {isOwner && canInviteMore && (
              <Dialog open={inviteDialogOpen} onOpenChange={(open) => { if (!open) handleCloseInviteDialog(); else setInviteDialogOpen(true); }}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="invite-user-btn">
                    <Plus className="w-4 h-4 mr-1" />
                    Invite
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Invite User</DialogTitle>
                    <DialogDescription>
                      Invite your spouse or accountant to access your data.
                    </DialogDescription>
                  </DialogHeader>
                  
                  {!inviteSuccess ? (
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Email Address</Label>
                        <Input
                          type="email"
                          placeholder="spouse@example.com"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          className="h-12"
                          data-testid="invite-email-input"
                        />
                      </div>
                      <Button
                        onClick={handleCreateInvite}
                        disabled={householdLoading || !inviteEmail.trim()}
                        className="w-full h-12 rounded-full font-semibold"
                        data-testid="create-invite-btn"
                      >
                        {householdLoading && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                        <Mail className="w-4 h-4 mr-2" />
                        Send Invite
                      </Button>
                      <p className="text-xs text-muted-foreground text-center">
                        They'll automatically join your account when they register with this email.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4 py-4 text-center">
                      <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mx-auto">
                        <Check className="w-8 h-8 text-green-500" />
                      </div>
                      <div>
                        <p className="font-medium text-lg">Invite Created!</p>
                        <p className="text-sm text-muted-foreground mt-1">
                          When <span className="font-medium">{inviteEmail}</span> creates an account, they'll automatically be linked to yours.
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        onClick={handleCloseInviteDialog}
                        className="w-full h-12"
                      >
                        Done
                      </Button>
                    </div>
                  )}
                </DialogContent>
              </Dialog>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {isOwner 
              ? `Users with access to your data. You can add up to 2 additional users.`
              : "Other users in your shared account."}
          </p>
          
          {/* Current Users */}
          <div className="space-y-2">
            {householdUsers.map((hUser) => (
              <div 
                key={hUser.id} 
                className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                data-testid={`household-user-${hUser.id}`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      {hUser.name}
                      {hUser.is_owner && (
                        <span className="text-xs bg-primary/20 text-primary px-2 py-0.5 rounded-full">
                          Owner
                        </span>
                      )}
                      {hUser.id === user?.id && (
                        <span className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full">
                          You
                        </span>
                      )}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {hUser.email} • {hUser.role}
                    </p>
                  </div>
                </div>
                {isOwner && !hUser.is_owner && hUser.id !== user?.id && (
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setRemoveUserId(hUser.id)}
                    data-testid={`remove-user-${hUser.id}`}
                  >
                    <UserMinus className="w-4 h-4 text-destructive" />
                  </Button>
                )}
              </div>
            ))}
          </div>

          {/* Pending Invites */}
          {isOwner && pendingInvites.length > 0 && (
            <div className="pt-4 border-t border-border">
              <p className="text-sm font-medium mb-2">Pending Invites</p>
              <div className="space-y-2">
                {pendingInvites.map((invite) => (
                  <div 
                    key={invite.id}
                    className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg"
                    data-testid={`pending-invite-${invite.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <Clock className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="font-medium">{invite.email}</p>
                        <p className="text-xs text-muted-foreground">
                          Waiting for them to register
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteInvite(invite.id)}
                      data-testid={`cancel-invite-${invite.id}`}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Limit reached message */}
          {isOwner && !canInviteMore && (
            <p className="text-sm text-muted-foreground text-center py-2">
              Maximum of 2 additional users reached.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Theme Toggle */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            {isDark ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            APPEARANCE
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Dark Mode</p>
              <p className="text-sm text-muted-foreground">
                Better for night driving
              </p>
            </div>
            <Switch
              checked={isDark}
              onCheckedChange={toggleTheme}
              data-testid="dark-mode-switch"
            />
          </div>
        </CardContent>
      </Card>

      {/* Per Diem Rates */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
            <DollarSign className="w-5 h-5" />
            PER DIEM RATES
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            IRS per diem rates for truck drivers. Update when rates change (typically October).
          </p>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Partial Day Rate</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={perDiemSettings.partial_rate}
                  onChange={(e) => setPerDiemSettings(s => ({ ...s, partial_rate: parseFloat(e.target.value) || 0 }))}
                  className="h-12 pl-7 text-lg font-semibold"
                  data-testid="partial-rate-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Departure/return days</p>
            </div>
            <div className="space-y-2">
              <Label>Full Day Rate</Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">$</span>
                <Input
                  type="number"
                  value={perDiemSettings.full_rate}
                  onChange={(e) => setPerDiemSettings(s => ({ ...s, full_rate: parseFloat(e.target.value) || 0 }))}
                  className="h-12 pl-7 text-lg font-semibold"
                  data-testid="full-rate-input"
                />
              </div>
              <p className="text-xs text-muted-foreground">Full days on road</p>
            </div>
          </div>
          
          <Button
            onClick={handleSavePerDiem}
            disabled={saving}
            className="w-full h-12 rounded-full font-semibold"
            data-testid="save-perdiem-rates-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Save Per Diem Rates
          </Button>
        </CardContent>
      </Card>

      {/* Custom Categories */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <Tag className="w-5 h-5" />
              EXPENSE CATEGORIES
            </CardTitle>
            <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="add-category-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Custom Category</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Category Name</Label>
                    <Input
                      placeholder="e.g., Truck Wash"
                      value={newCategoryName}
                      onChange={(e) => setNewCategoryName(e.target.value)}
                      className="h-12"
                      data-testid="new-category-name"
                    />
                  </div>
                  <Button
                    onClick={handleAddCategory}
                    className="w-full h-12 rounded-full font-semibold"
                    data-testid="save-new-category-btn"
                  >
                    Add Category
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Default trucking categories are included. Add custom categories below.
          </p>
          
          {customCategories.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No custom categories yet
            </p>
          ) : (
            <div className="space-y-2">
              {customCategories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                  data-testid={`custom-category-${cat.id}`}
                >
                  <span className="font-medium">{cat.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteCategory(cat.id)}
                    data-testid={`delete-category-${cat.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Revenue Categories */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg font-['Barlow_Condensed'] flex items-center gap-2">
              <Truck className="w-5 h-5" />
              REVENUE CATEGORIES
            </CardTitle>
            <Dialog open={revCategoryDialogOpen} onOpenChange={setRevCategoryDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" size="sm" data-testid="add-rev-category-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Add
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Add Revenue Category</DialogTitle>
                  <DialogDescription>
                    Add a custom revenue line item type for your loads.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Category Name</Label>
                    <Input
                      placeholder="e.g., Deadhead Pay"
                      value={newRevCategoryName}
                      onChange={(e) => setNewRevCategoryName(e.target.value)}
                      className="h-12"
                      data-testid="new-rev-category-name"
                    />
                  </div>
                  <Button
                    onClick={handleAddRevenueCategory}
                    className="w-full h-12 rounded-full font-semibold"
                    data-testid="save-new-rev-category-btn"
                  >
                    Add Category
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground mb-4">
            Default revenue types are included. Add custom categories for your loads below.
          </p>
          
          {customRevenueCategories.length === 0 ? (
            <p className="text-center text-muted-foreground py-4">
              No custom revenue categories yet
            </p>
          ) : (
            <div className="space-y-2">
              {customRevenueCategories.map((cat) => (
                <div 
                  key={cat.id} 
                  className="flex items-center justify-between p-3 bg-secondary rounded-lg"
                  data-testid={`custom-rev-category-${cat.id}`}
                >
                  <span className="font-medium">{cat.name}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleDeleteRevenueCategory(cat.id)}
                    data-testid={`delete-rev-category-${cat.id}`}
                  >
                    <Trash2 className="w-4 h-4 text-destructive" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Remove User Confirmation */}
      <AlertDialog open={!!removeUserId} onOpenChange={() => setRemoveUserId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove User?</AlertDialogTitle>
            <AlertDialogDescription>
              This will remove this user from your account. They will no longer have access to your expenses and revenue data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-remove-user">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleRemoveUser} data-testid="confirm-remove-user">
              Remove User
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
